import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  mergeClasses,
  Spinner,
  Textarea,
} from "@fluentui/react-components";
import {
  ArrowLeftRegular,
  ArrowRightRegular,
  CheckmarkCircleFilled,
  CheckmarkRegular,
  DataTrendingRegular,
  DismissRegular,
  DocumentRegular,
  PeopleRegular,
  PersonChatRegular,
  SparkleRegular,
} from "@fluentui/react-icons";
import {
  deleteQuestionBank,
  deleteDiagnosticLogo,
  registerDiagnosticLogo,
} from "apps/diagnostics/api";
import { BrandingPreview } from "apps/diagnostics/components/manageDiagnostics/editDialog/brandingPreview/BrandingPreview";
import { DemographicsTab } from "apps/diagnostics/components/manageDiagnostics/editDialog/DemographicsTab";
import { buildAccentPalette } from "utils/accentPalette";
import {
  addProjectIqWorkspaceDocument,
  getSurveyTemplates,
  registerDiagnosticQuestionBank,
} from "apps/project-iq/api";
import { DIAGNOSTIC_TEMPLATE_CATEGORIES } from "apps/project-iq/constants/diagnosticTemplates";
import { IDiagnosticTemplate } from "apps/project-iq/models/IDiagnosticTemplate";
import {
  formatFileSize,
  getFileTypeInfo,
} from "apps/project-iq/utils/fileUtils";
import { formatDate } from "utils/generalUtils";
import {
  DemographicsPlacement,
  DiagnosticMethod,
  IDiagnostic,
  QuestionBankType,
} from "apps/project-iq/models/IDiagnostic";
import { RichTextField } from "components/cells/RichTextFieldCell";
import { DialogFooterAction } from "components/dialogFooterAction/DialogFooterAction";
import { DialogTabs } from "components/dialogTabs/DialogTabs";
import { CustomToggle } from "components/customToggle/CustomToggle";
import { FileDropzone } from "components/fileDropzone/FileDropzone";
import { FileWithId } from "models/FileWithId";
import { IFile } from "models/IFile";
import { ColumnTypes } from "models/enums/ColumnTypes";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useSharedDialogContentStyles,
  useSharedDialogFooterStyles,
  useSharedFileCardStyles,
} from "theme/sharedStyles";
import { uploadFilesToStorage, uploadLogoToStorage } from "utils/blobUtils";
import { useCreateDiagnostic } from "../../hooks/useCreateDiagnostic";
import { useUpdateDiagnostic } from "../../hooks/useUpdateDiagnostic";
import { useStyles } from "./CreateDiagnosticDialog.styles";
import {
  CreateDiagnosticStep,
  DIALOG_LABELS,
  ICreateDiagnosticDialogProps,
  IDeliveryMethodOption,
  STEP_LABELS,
} from "./CreateDiagnosticDialog.types";

const DELIVERY_METHOD_OPTIONS: IDeliveryMethodOption[] = [
  {
    value: "survey",
    label: "Survey",
    description: "Quantitative diagnostics at scale via SurveyMonkey.",
    icon: <DataTrendingRegular />,
  },
  {
    value: "ai_interview",
    label: "AI Interview",
    description: "AI-led 1:1 conversations with transcription and themes.",
    icon: <SparkleRegular />,
  },
  {
    value: "workshops_focus_groups",
    label: "Workshops & Focus Groups",
    description: "Facilitated group sessions to surface perspectives.",
    icon: <PeopleRegular />,
  },
  {
    value: "in_person_interviews",
    label: "In-Person Interviews",
    description: "Structured 1:1 conversations using NH frameworks.",
    icon: <PersonChatRegular />,
  },
];

const QUESTION_BANK_OPTIONS: {
  value: QuestionBankType;
  label: string;
  description: string;
}[] = [
    {
      value: "template",
      label: "Template Question Bank",
      description:
        "Start from an NH-curated set of questions for this diagnostic.",
    },
    {
      value: "custom",
      label: "Custom Question Bank",
      description: "Upload your own questions from an Excel file.",
    },
  ];

const DEFAULT_HEADER_COLOR = "#102a43";
const DEFAULT_ACCENT_COLOR = "#039fb8";

const CONTEXT_ATTACHMENT_EXTENSIONS = [
  ".xlsx",
  ".xls",
  ".json",
  ".epub",
  ".rtf",
  ".odt",
  ".html",
  ".txt",
  ".csv",
  ".docx",
  ".pdf",
  ".jpeg",
  ".png",
  ".jpg",
  ".gif",
  ".webp",
  ".md",
];

const LOGO_EXTENSIONS = [".png", ".jpg", ".jpeg", ".svg"];

const QUESTION_BANK_EXTENSIONS = [".xlsx", ".xls"];

export const CreateDiagnosticDialog = ({
  isOpen,
  workspaceId,
  onDismiss,
  onSaved,
  mode = "create",
  diagnostic,
}: ICreateDiagnosticDialogProps): JSX.Element => {
  const footerStyles = useSharedDialogFooterStyles();
  const contentStyles = useSharedDialogContentStyles();
  const fileCardStyles = useSharedFileCardStyles();
  const styles = useStyles();

  const [activeStep, setActiveStep] =
    useState<CreateDiagnosticStep>("basic-info");
  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [method, setMethod] = useState<DiagnosticMethod | null>(null);
  const [questionBankType, setQuestionBankType] =
    useState<QuestionBankType | null>(null);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string>("");
  const [saveError, setSaveError] = useState<string>("");
  const [background, setBackground] = useState<string>("");
  const [beginningMessage, setBeginningMessage] = useState<string>("");
  const [endingMessage, setEndingMessage] = useState<string>("");
  const [headerColor, setHeaderColor] = useState<string>(DEFAULT_HEADER_COLOR);
  const [accentColor, setAccentColor] = useState<string>(DEFAULT_ACCENT_COLOR);
  const [customBrandingEnabled, setCustomBrandingEnabled] =
    useState<boolean>(false);
  const [isDemographicsEnabled, setIsDemographicsEnabled] =
    useState<boolean>(false);
  const [demographicsQuestions, setDemographicsQuestions] = useState<string[]>(
    [],
  );
  const [demographicsPlacement, setDemographicsPlacement] =
    useState<DemographicsPlacement>("beginning");
  const [demographicsQuestionOverrides, setDemographicsQuestionOverrides] =
    useState<Record<string, string>>({});
  const [contextAttachments, setContextAttachments] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<IFile[]>([]);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [questionBankFile, setQuestionBankFile] = useState<File | null>(null);
  const [isCustomQuestionBankRemoved, setIsCustomQuestionBankRemoved] =
    useState<boolean>(false);
  const [existingQuestionBankMeta, setExistingQuestionBankMeta] = useState<{
    size: number;
    lastModified: number;
  } | null>(null);
  const [headerLogoUrl, setHeaderLogoUrl] = useState<string>("");
  const [surveyTemplates, setSurveyTemplates] = useState<IDiagnosticTemplate[]>(
    [],
  );
  const [isSurveyTemplatesLoading, setIsSurveyTemplatesLoading] =
    useState<boolean>(false);

  useEffect((): void => {
    const url = diagnostic?.customQuestionBankUrl;
    if (!url) {
      setExistingQuestionBankMeta(null);
      return;
    }
    fetch(url, { method: "HEAD" })
      .then((res) => {
        const size = parseInt(res.headers.get("Content-Length") ?? "0", 10);
        const lastModified = new Date(
          res.headers.get("Last-Modified") ?? 0,
        ).getTime();
        setExistingQuestionBankMeta({ size, lastModified });
      })
      .catch(() => setExistingQuestionBankMeta(null));
  }, [diagnostic?.customQuestionBankUrl]);

  useEffect((): void => {
    setIsSurveyTemplatesLoading(true);
    getSurveyTemplates()
      .then((data: IDiagnosticTemplate[]) => setSurveyTemplates(data))
      .catch(() => setSurveyTemplates([]))
      .finally(() => setIsSurveyTemplatesLoading(false));
  }, []);

  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { mutateAsync: createDiagnostic } = useCreateDiagnostic(workspaceId);
  const { mutateAsync: updateDiagnosticMutation } =
    useUpdateDiagnostic(workspaceId);
  const isSaving = isSubmitting;

  const createButtonRef = useRef<HTMLSpanElement>(null);
  const templatePanelRef = useRef<HTMLDivElement>(null);
  const questionBankPanelRef = useRef<HTMLDivElement>(null);

  const stepOrder = useMemo<CreateDiagnosticStep[]>(() => {
    if (method === "ai_interview") {
      return [
        "basic-info",
        "delivery-method",
        "context",
        "messages",
        ...(customBrandingEnabled
          ? (["branding"] as CreateDiagnosticStep[])
          : []),
        ...(isDemographicsEnabled
          ? (["demographics"] as CreateDiagnosticStep[])
          : []),
      ];
    }
    if (method === "survey") {
      if (questionBankType === "template") {
        return ["basic-info", "delivery-method", "template"];
      }
      if (questionBankType === "custom") {
        return ["basic-info", "delivery-method", "question-bank-upload"];
      }
      return ["basic-info", "delivery-method"];
    }
    return ["basic-info", "delivery-method"];
  }, [method, questionBankType, customBrandingEnabled, isDemographicsEnabled]);

  useEffect((): void => {
    if (!stepOrder.includes(activeStep)) {
      setActiveStep(stepOrder[stepOrder.length - 1] ?? "basic-info");
    }
  }, [stepOrder, activeStep]);

  const tabs = useMemo(
    () => stepOrder.map((key) => ({ key, label: STEP_LABELS[key] })),
    [stepOrder],
  );

  const isLastStep = activeStep === stepOrder[stepOrder.length - 1];

  useEffect((): void => {
    if (isLastStep) {
      createButtonRef.current
        ?.querySelector<HTMLButtonElement>("button")
        ?.focus();
    }
  }, [isLastStep]);

  useEffect((): void => {
    if (activeStep === "template") {
      const firstCard =
        templatePanelRef.current?.querySelector<HTMLButtonElement>(
          '[role="radio"]',
        );
      firstCard?.focus();
    }
  }, [activeStep]);

  useEffect((): void => {
    if (activeStep === "question-bank-upload") {
      questionBankPanelRef.current
        ?.querySelector<HTMLElement>("input, button, [tabindex='0']")
        ?.focus();
    }
  }, [activeStep]);

  useEffect((): void => {
    if (activeStep === "question-bank-upload") {
      questionBankPanelRef.current
        ?.querySelector<HTMLElement>("input, button, [tabindex='0']")
        ?.focus();
    }
  }, [activeStep]);

  useEffect((): void => {
    if (isOpen && mode === "edit" && diagnostic) {
      setActiveStep("basic-info");
      setName(diagnostic.name);
      setDescription(diagnostic.description ?? "");
      setMethod(diagnostic.method);
      setQuestionBankType(diagnostic.questionBankType ?? null);
      setTemplateId(diagnostic.templateId ?? null);
      setNameError("");
      setSaveError("");
      setBackground(diagnostic.background ?? "");
      setBeginningMessage(diagnostic.beginningMessage ?? "");
      setEndingMessage(diagnostic.endingMessage ?? "");
      setHeaderColor(diagnostic.headerColor ?? DEFAULT_HEADER_COLOR);
      setAccentColor(diagnostic.accentColor ?? DEFAULT_ACCENT_COLOR);
      setCustomBrandingEnabled(diagnostic.customBrandingEnabled ?? false);
      const hasDemographics =
        (diagnostic.demographicsQuestions ?? []).length > 0;
      setIsDemographicsEnabled(hasDemographics);
      setDemographicsQuestions(diagnostic.demographicsQuestions ?? []);
      setDemographicsPlacement(diagnostic.demographicsPlacement ?? "beginning");
      setDemographicsQuestionOverrides(
        diagnostic.demographicsQuestionOverrides ?? {},
      );
      setContextAttachments([]);
      setExistingAttachments(diagnostic.attachments ?? []);
      setLogoFile(null);
      setQuestionBankFile(null);
      setIsCustomQuestionBankRemoved(false);
      setHeaderLogoUrl(diagnostic.headerLogoUrl ?? "");
    } else if (isOpen) {
      setActiveStep("basic-info");
      setName("");
      setDescription("");
      setMethod(null);
      setQuestionBankType(null);
      setTemplateId(null);
      setNameError("");
      setSaveError("");
      setBackground("");
      setBeginningMessage("");
      setEndingMessage("");
      setHeaderColor(DEFAULT_HEADER_COLOR);
      setAccentColor(DEFAULT_ACCENT_COLOR);
      setCustomBrandingEnabled(false);
      setIsDemographicsEnabled(false);
      setDemographicsQuestions([]);
      setDemographicsPlacement("beginning");
      setDemographicsQuestionOverrides({});
      setContextAttachments([]);
      setExistingAttachments([]);
      setLogoFile(null);
      setQuestionBankFile(null);
      setIsCustomQuestionBankRemoved(false);
      setHeaderLogoUrl("");
    }
  }, [isOpen]);

  const handleNameChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setName(event.target.value);
    if (nameError) {
      setNameError("");
    }
  };

  const handleDescriptionChange = (
    _event: React.ChangeEvent<HTMLTextAreaElement>,
    data: { value: string },
  ): void => {
    setDescription(data.value);
  };

  const handleMethodChange = (value: DiagnosticMethod): void => {
    setMethod(value);
    if (value !== "survey") {
      setQuestionBankType(null);
      setTemplateId(null);
    }
  };

  const handleQuestionBankChange = (value: QuestionBankType): void => {
    setQuestionBankType(value);
    if (value !== "template") {
      setTemplateId(null);
    }
  };

  const handleRadioKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    count: number,
    currentIndex: number,
    onChange: (nextIndex: number) => void,
  ): void => {
    let nextIndex: number | null = null;
    if (event.key === "ArrowDown" || event.key === "ArrowRight") {
      nextIndex = (currentIndex + 1) % count;
    } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
      nextIndex = (currentIndex - 1 + count) % count;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = count - 1;
    }
    if (nextIndex !== null) {
      event.preventDefault();
      onChange(nextIndex);
      const group = event.currentTarget.closest('[role="radiogroup"]');
      const buttons =
        group?.querySelectorAll<HTMLButtonElement>('[role="radio"]');
      buttons?.[nextIndex]?.focus();
    }
  };

  const handleLogoFileSelect = (files: File[]): void => {
    if (files.length === 0) return;
    const file = files[0];
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        setHeaderLogoUrl(e.target.result as string);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleLogoFileRemove = (): void => {
    setLogoFile(null);
    setHeaderLogoUrl("");
  };

  const handleNext = (): void => {
    if (activeStep === "basic-info") {
      const trimmedName = name.trim();
      if (!trimmedName) {
        setNameError(DIALOG_LABELS.NAME_REQUIRED);
        return;
      }
      setName(trimmedName);
    }
    const nextStep = stepOrder[stepOrder.indexOf(activeStep) + 1];
    if (nextStep) setActiveStep(nextStep);
  };

  const handleBack = (): void => {
    const prevStep = stepOrder[stepOrder.indexOf(activeStep) - 1];
    if (prevStep) setActiveStep(prevStep);
  };

  const handleTabClick = (tab: string): void => {
    const target = tab as CreateDiagnosticStep;
    const currentIndex = stepOrder.indexOf(activeStep);
    const targetIndex = stepOrder.indexOf(target);
    if (targetIndex > currentIndex) {
      const trimmedName = name.trim();
      if (!trimmedName) {
        setNameError(DIALOG_LABELS.NAME_REQUIRED);
        return;
      }
      setName(trimmedName);
    }
    setActiveStep(target);
  };

  const handleSubmit = async (): Promise<void> => {
    if (!method) return;
    setSaveError("");
    setIsSubmitting(true);

    if (mode === "edit" && diagnostic) {
      try {
        let resolvedLogoUrl: string | undefined;
        if (logoFile) {
          if (diagnostic.headerLogoUrl?.startsWith("http")) {
            try {
              await deleteDiagnosticLogo(diagnostic.id);
            } catch {
              // non-fatal
            }
          }
          const blobName = await uploadLogoToStorage(
            logoFile,
            "diagnostics-logo",
            diagnostic.id,
          );
          const logoResult = await registerDiagnosticLogo(
            diagnostic.id,
            blobName,
          );
          resolvedLogoUrl = logoResult.url;
        } else {
          resolvedLogoUrl = headerLogoUrl.startsWith("http")
            ? headerLogoUrl
            : undefined;
        }

        let uploadedAttachments: IFile[] = [];
        if (contextAttachments.length > 0) {
          uploadedAttachments = (
            await uploadFilesToStorage(
              contextAttachments.map((file) => ({ file })),
              "diagnostics",
              diagnostic.id,
            )
          ).filter((uploadedFile) => !!uploadedFile.url);
        }
        
        let resolvedCustomQuestionBankUrl: string | null | undefined;
        if (questionBankFile) {
          if (diagnostic.customQuestionBankUrl?.startsWith("http")) {
            try {
              await deleteQuestionBank(diagnostic.id);
            } catch {
              setSaveError(DIALOG_LABELS.ERROR_DELETE_QUESTION_BANK);
              setIsSubmitting(false);
              return;
            }
          }
          const [uploaded] = await uploadFilesToStorage(
            [{ file: questionBankFile }],
            "diagnostics",
            diagnostic.id,
          );
          if (uploaded) {
            const result = await registerDiagnosticQuestionBank(
              diagnostic.id,
              uploaded.url,
            );
            resolvedCustomQuestionBankUrl = result.url;
          }
        } else if (isCustomQuestionBankRemoved) {
          if (diagnostic.customQuestionBankUrl?.startsWith("http")) {
            try {
              await deleteQuestionBank(diagnostic.id);
            } catch {
              setSaveError(DIALOG_LABELS.ERROR_DELETE_QUESTION_BANK);
              setIsSubmitting(false);
              return;
            }
          }
          resolvedCustomQuestionBankUrl = null;
        } else {
          resolvedCustomQuestionBankUrl = diagnostic.customQuestionBankUrl;
        }

        await updateDiagnosticMutation({
          id: diagnostic.id,
          diagnostic: {
            ...diagnostic,
            name: name.trim(),
            description: description.trim(),
            questionBankType,
            templateId: questionBankType === "template" ? templateId : null,
            customQuestionBankUrl: resolvedCustomQuestionBankUrl,
            attachments: [
              ...[...uploadedAttachments].reverse(),
              ...existingAttachments,
            ],
            background: background.trim() || undefined,
            beginningMessage: beginningMessage.trim() || undefined,
            endingMessage: endingMessage.trim() || undefined,
            headerColor,
            headerLogoUrl: resolvedLogoUrl,
            accentColor,
            customBrandingEnabled,
            demographicsQuestions,
            demographicsPlacement,
            demographicsQuestionOverrides,
          } as IDiagnostic,
        });
        try {
          await registerWorkspaceDocuments(uploadedAttachments);
        } catch {
          // The diagnostic is already saved with its attachments; the
          // workspace library entry can be added later from the workspace.
        }
        onSaved?.();
        onDismiss();
      } catch {
        setSaveError(DIALOG_LABELS.ERROR_UPDATE);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    let createdDiagnosticId: string;
    let createdDiagnostic: IDiagnostic;
    try {
      createdDiagnostic = await createDiagnostic({
        name: name.trim(),
        description: description.trim(),
        method,
        questionBankType,
        templateId,
        background: background.trim() || undefined,
        beginningMessage: beginningMessage.trim() || undefined,
        endingMessage: endingMessage.trim() || undefined,
        headerColor,
        accentColor,
        customBrandingEnabled,
        demographicsQuestions,
        demographicsPlacement,
        demographicsQuestionOverrides,
      });
      createdDiagnosticId = createdDiagnostic.id;
    } catch {
      setSaveError(DIALOG_LABELS.ERROR_CREATE);
      setIsSubmitting(false);
      return;
    }

    try {
      if (contextAttachments.length > 0) {
        const attachmentFiles: FileWithId[] = contextAttachments.map(
          (file) => ({ file }),
        );
        const uploadedAttachments = (
          await uploadFilesToStorage(
            attachmentFiles,
            "diagnostics",
            createdDiagnosticId,
          )
        ).filter((uploadedFile) => !!uploadedFile.url);
        await updateDiagnosticMutation({
          id: createdDiagnosticId,
          diagnostic: {
            ...createdDiagnostic,
            attachments: [...uploadedAttachments].reverse(),
          },
        });
        await registerWorkspaceDocuments(uploadedAttachments);
      }

      if (logoFile) {
        const blobName = await uploadLogoToStorage(
          logoFile,
          "diagnostics-logo",
          createdDiagnosticId,
        );
        await registerDiagnosticLogo(createdDiagnosticId, blobName);
      }

      if (questionBankFile) {
        const [uploaded] = await uploadFilesToStorage(
          [{ file: questionBankFile }],
          "diagnostics",
          createdDiagnosticId,
        );
        if (uploaded) {
          await registerDiagnosticQuestionBank(
            createdDiagnosticId,
            uploaded.url,
          );
        }
      }
    } catch {
      // diagnostic created — files can be added later in the Edit flow
    }

    onDismiss();
    navigate(
      `/workspace/${workspaceId}/nh-diagnostic-iq/${createdDiagnosticId}`,
    );
  };

  const registerWorkspaceDocuments = async (files: IFile[]): Promise<void> => {
    for (const uploadedFile of files) {
      await addProjectIqWorkspaceDocument({
        workspaceId,
        filename: uploadedFile.url,
        originalFilename: uploadedFile.filename,
        source: "file",
      });
    }
  };

  const isNameEmpty = !name.trim();

  const isNextDisabled =
    (activeStep === "basic-info" && isNameEmpty) ||
    (activeStep === "delivery-method" && method === null);

  const isSubmitDisabled =
    method === null ||
    (method === "survey" && questionBankType === null) ||
    (method === "survey" &&
      questionBankType === "template" &&
      templateId === null) ||
    isSaving;

  return (
    <Dialog
      open={isOpen}
      modalType="modal"
      onOpenChange={(_, data) => {
        if (!data.open) onDismiss();
      }}
    >
      <DialogSurface
        className={mergeClasses(
          contentStyles.dialogSurface,
          styles.wideDialogSurface,
        )}
      >
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                aria-label={DIALOG_LABELS.CLOSE}
                onClick={onDismiss}
              />
            }
          >
            {mode === "edit" ? DIALOG_LABELS.TITLE_EDIT : DIALOG_LABELS.TITLE}
          </DialogTitle>
          <DialogContent
            className={mergeClasses(
              contentStyles.tabbedDialogContent,
              styles.dialogContent,
            )}
          >
            {isSaving && (
              <div
                className={contentStyles.formLoadingOverlay}
                aria-live="assertive"
                aria-atomic="true"
              >
                <Spinner
                  label={
                    mode === "edit"
                      ? `${DIALOG_LABELS.SAVING_EDIT}...`
                      : `${DIALOG_LABELS.SAVING} diagnostic...`
                  }
                />
              </div>
            )}
            <DialogTabs
              tabs={tabs}
              activeTab={activeStep}
              onTabClick={handleTabClick}
            />
            <div
              className={mergeClasses(
                contentStyles.formContainer,
                contentStyles.scrollableContent,
              )}
            >
              {activeStep === "basic-info" && (
                <div
                  className={contentStyles.tabContent}
                  role="tabpanel"
                  id="tabpanel-basic-info"
                  aria-labelledby="tab-basic-info"
                >
                  <div className={styles.formContainer}>
                    <Field
                      label={DIALOG_LABELS.NAME_LABEL}
                      required
                      validationState={nameError ? "error" : "none"}
                      validationMessage={nameError || undefined}
                    >
                      <Input
                        value={name}
                        onChange={handleNameChange}
                        placeholder={DIALOG_LABELS.NAME_PLACEHOLDER}
                        disabled={isSaving}
                      />
                    </Field>
                    <Field label={DIALOG_LABELS.DESCRIPTION_LABEL}>
                      <Textarea
                        value={description}
                        onChange={handleDescriptionChange}
                        resize="vertical"
                        rows={3}
                        disabled={isSaving}
                      />
                    </Field>
                  </div>
                </div>
              )}

              {activeStep === "delivery-method" && (
                <div
                  className={contentStyles.tabContent}
                  role="tabpanel"
                  id="tabpanel-delivery-method"
                  aria-labelledby="tab-delivery-method"
                >
                  <p className={styles.methodHelperText}>
                    {DIALOG_LABELS.METHOD_HELPER}
                  </p>
                  <div
                    className={mergeClasses(
                      styles.methodGrid,
                      mode === "edit" && styles.methodGridLocked,
                    )}
                    role="radiogroup"
                    aria-label={DIALOG_LABELS.METHOD_HEADING}
                    aria-disabled={mode === "edit"}
                  >
                    {DELIVERY_METHOD_OPTIONS.map((option, index) => {
                      const isSelected = method === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          role="radio"
                          aria-checked={isSelected}
                          disabled={mode === "edit"}
                          tabIndex={
                            isSelected || (method === null && index === 0)
                              ? 0
                              : -1
                          }
                          className={mergeClasses(
                            styles.methodCard,
                            isSelected && styles.methodCardSelected,
                            mode === "edit" && styles.methodCardEditLocked,
                          )}
                          onClick={() => handleMethodChange(option.value)}
                          onKeyDown={(event) =>
                            handleRadioKeyDown(
                              event,
                              DELIVERY_METHOD_OPTIONS.length,
                              index,
                              (i) =>
                                handleMethodChange(
                                  DELIVERY_METHOD_OPTIONS[i].value,
                                ),
                            )
                          }
                        >
                          <div className={styles.methodCardRadio}>
                            {isSelected ? (
                              <CheckmarkCircleFilled
                                className={styles.radioCheckmark}
                                aria-hidden="true"
                              />
                            ) : (
                              <div
                                className={styles.radioCircle}
                                aria-hidden="true"
                              />
                            )}
                          </div>
                          <div
                            className={mergeClasses(
                              styles.methodCardIcon,
                              isSelected && styles.methodCardIconSelected,
                            )}
                            aria-hidden="true"
                          >
                            {option.icon}
                          </div>
                          <div className={styles.methodCardText}>
                            <span className={styles.methodCardLabel}>
                              {option.label}
                            </span>
                            <span className={styles.methodCardDescription}>
                              {option.description}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {method === "ai_interview" && (
                    <div className={styles.aiToggleSection}>
                      <CustomToggle
                        checked={customBrandingEnabled}
                        onChange={setCustomBrandingEnabled}
                        label={DIALOG_LABELS.CUSTOM_BRANDING_LABEL}
                        hint={DIALOG_LABELS.CUSTOM_BRANDING_HINT}
                        disabled={isSaving}
                      />
                      <CustomToggle
                        checked={isDemographicsEnabled}
                        onChange={(checked) => {
                          setIsDemographicsEnabled(checked);
                          if (!checked) {
                            setDemographicsQuestions([]);
                            setDemographicsPlacement("beginning");
                            setDemographicsQuestionOverrides({});
                          }
                        }}
                        label={DIALOG_LABELS.DEMOGRAPHICS_LABEL}
                        hint={DIALOG_LABELS.DEMOGRAPHICS_HINT}
                        disabled={isSaving}
                      />
                    </div>
                  )}

                  {method === "survey" && (
                    <section
                      className={styles.questionBankSection}
                      aria-label={DIALOG_LABELS.QUESTION_BANK_SECTION}
                    >
                      <p className={styles.questionBankSectionHeading}>
                        {DIALOG_LABELS.QUESTION_BANK_SECTION}
                      </p>
                      <p className={styles.questionBankSectionHelper}>
                        {DIALOG_LABELS.QUESTION_BANK_SECTION_HELPER}
                      </p>
                      <div
                        className={styles.questionBankGrid}
                        role="radiogroup"
                        aria-label="Select question bank type"
                      >
                        {QUESTION_BANK_OPTIONS.map((option, index) => {
                          const isSelected = questionBankType === option.value;
                          return (
                            <button
                              key={option.value}
                              type="button"
                              role="radio"
                              aria-checked={isSelected}
                              tabIndex={
                                isSelected ||
                                  (questionBankType === null && index === 0)
                                  ? 0
                                  : -1
                              }
                              className={mergeClasses(
                                styles.methodCard,
                                isSelected && styles.methodCardSelected,
                              )}
                              onClick={() =>
                                handleQuestionBankChange(option.value)
                              }
                              onKeyDown={(event) =>
                                handleRadioKeyDown(
                                  event,
                                  QUESTION_BANK_OPTIONS.length,
                                  index,
                                  (i) =>
                                    handleQuestionBankChange(
                                      QUESTION_BANK_OPTIONS[i].value,
                                    ),
                                )
                              }
                            >
                              <div className={styles.methodCardRadio}>
                                {isSelected ? (
                                  <CheckmarkCircleFilled
                                    className={styles.radioCheckmark}
                                    aria-hidden="true"
                                  />
                                ) : (
                                  <div
                                    className={styles.radioCircle}
                                    aria-hidden="true"
                                  />
                                )}
                              </div>
                              <div className={styles.methodCardText}>
                                <span className={styles.methodCardLabel}>
                                  {option.label}
                                </span>
                                <span className={styles.methodCardDescription}>
                                  {option.description}
                                </span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </section>
                  )}
                </div>
              )}

              {activeStep === "context" && (
                <div
                  className={contentStyles.tabContent}
                  role="tabpanel"
                  id="tabpanel-context"
                  aria-labelledby="tab-context"
                >
                  <p className={styles.methodHelperText}>
                    {DIALOG_LABELS.CONTEXT_HELPER}
                  </p>
                  <Field label={DIALOG_LABELS.CONTEXT_LABEL}>
                    <Textarea
                      value={background}
                      onChange={(_, data) => setBackground(data.value)}
                      resize="vertical"
                      rows={5}
                      disabled={isSaving}
                      placeholder={DIALOG_LABELS.CONTEXT_PLACEHOLDER}
                    />
                  </Field>
                  <Field label={DIALOG_LABELS.ATTACHMENTS_LABEL}>
                    <FileDropzone
                      onFilesSelected={(files) =>
                        setContextAttachments((prev) => {
                          const existingNames = new Set(
                            prev.map((f) => f.name),
                          );
                          return [
                            ...prev,
                            ...files.filter((f) => !existingNames.has(f.name)),
                          ];
                        })
                      }
                      acceptedExtensions={CONTEXT_ATTACHMENT_EXTENSIONS}
                      disabled={isSaving}
                    />
                    {(existingAttachments.length > 0 ||
                      contextAttachments.length > 0) && (
                        <div
                          className={mergeClasses(
                            fileCardStyles.fileList,
                            styles.attachmentFileList,
                          )}
                        >
                          {[...contextAttachments].reverse().map((file) => (
                            <div
                              key={file.name}
                              className={fileCardStyles.fileCard}
                            >
                              <span className={fileCardStyles.fileInfo}>
                                <span className={fileCardStyles.fileName}>
                                  {file.name}
                                </span>
                              </span>
                              <div className={fileCardStyles.fileActions}>
                                <Button
                                  appearance="subtle"
                                  icon={<DismissRegular aria-hidden="true" />}
                                  aria-label={`Remove ${file.name}`}
                                  className={fileCardStyles.fileDeleteButton}
                                  disabled={isSaving}
                                  onClick={() =>
                                    setContextAttachments((prev) =>
                                      prev.filter((f) => f.name !== file.name),
                                    )
                                  }
                                />
                              </div>
                            </div>
                          ))}
                              {existingAttachments.map((file) => {
                            const displayName =
                              file.filename.split("/").pop() || file.filename;
                            return (
                              <div
                                key={file.url ?? file.filename}
                                className={fileCardStyles.fileCard}
                              >
                                <span className={fileCardStyles.fileInfo}>
                                  <span className={fileCardStyles.fileName}>
                                    {displayName}
                                  </span>
                                </span>
                                <div className={fileCardStyles.fileActions}>
                                  <Button
                                    appearance="subtle"
                                    icon={<DismissRegular aria-hidden="true" />}
                                    aria-label={`Remove ${displayName}`}
                                    className={fileCardStyles.fileDeleteButton}
                                    disabled={isSaving}
                                    onClick={() =>
                                      setExistingAttachments((prev) =>
                                        prev.filter((f) => f !== file),
                                      )
                                    }
                                  />
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                  </Field>
                </div>
              )}

              {activeStep === "messages" && (
                <div
                  className={contentStyles.tabContent}
                  role="tabpanel"
                  id="tabpanel-messages"
                  aria-labelledby="tab-messages"
                >
                  <p className={styles.methodHelperText}>
                    {DIALOG_LABELS.MESSAGES_HELPER}
                  </p>
                  <div className={styles.formContainer}>
                    <Field
                      label={DIALOG_LABELS.OPENING_MESSAGE_LABEL}
                      hint={DIALOG_LABELS.OPENING_MESSAGE_HINT}
                    >
                      <RichTextField
                        value={beginningMessage}
                        editable={!isSaving}
                        onChange={(_e, val) => setBeginningMessage(val ?? "")}
                        type={ColumnTypes.RichText}
                      />
                    </Field>
                    <Field
                      label={DIALOG_LABELS.CLOSING_MESSAGE_LABEL}
                      hint={DIALOG_LABELS.CLOSING_MESSAGE_HINT}
                    >
                      <RichTextField
                        value={endingMessage}
                        editable={!isSaving}
                        onChange={(_e, val) => setEndingMessage(val ?? "")}
                        type={ColumnTypes.RichText}
                      />
                    </Field>
                  </div>
                </div>
              )}

              {activeStep === "template" && (
                <div
                  ref={templatePanelRef}
                  className={contentStyles.tabContent}
                  role="tabpanel"
                  id="tabpanel-template"
                  aria-labelledby="tab-template"
                >
                  <div>
                    <p className={styles.methodHelperText}>
                      {DIALOG_LABELS.TEMPLATE_HEADING}
                    </p>
                    <p className={styles.templateBrowseRow}>
                      {DIALOG_LABELS.TEMPLATE_BROWSE_TEXT}{" "}
                      <a
                        href={DIALOG_LABELS.TEMPLATE_BROWSE_URL}
                        target="_blank"
                        rel="noreferrer"
                        className={styles.templateBrowseLink}
                      >
                        {DIALOG_LABELS.TEMPLATE_BROWSE_LINK}
                      </a>
                    </p>
                  </div>
                  {isSurveyTemplatesLoading ? (
                    <Spinner label={DIALOG_LABELS.LOADING_TEMPLATES} />
                  ) : null}
                  {DIAGNOSTIC_TEMPLATE_CATEGORIES.map((category) => {
                    const categoryTemplates = surveyTemplates.filter(
                      (t) => t.category === category.name,
                    );
                    if (categoryTemplates.length === 0) return null;
                    return (
                      <div
                        key={category.name}
                        className={styles.templateCategoryGroup}
                      >
                        <div className={styles.templateCategoryHeader}>
                          <div
                            className={styles.templateCategoryDot}
                            style={{ backgroundColor: category.color }}
                            aria-hidden="true"
                          />
                          <span className={styles.templateCategoryName}>
                            {category.name}
                          </span>
                          <span className={styles.templateCategoryCount}>
                            ({categoryTemplates.length})
                          </span>
                        </div>
                        <div className={styles.templateGrid}>
                          {categoryTemplates.map((template, index) => {
                            const isSelected = templateId === template.id;
                            return (
                              <button
                                key={template.id}
                                type="button"
                                role="radio"
                                aria-checked={isSelected}
                                tabIndex={
                                  isSelected ||
                                    (templateId === null && index === 0)
                                    ? 0
                                    : -1
                                }
                                className={mergeClasses(
                                  styles.templateCard,
                                  isSelected && styles.templateCardSelected,
                                )}
                                onClick={() => setTemplateId(template.id)}
                                onKeyDown={(event) =>
                                  handleRadioKeyDown(
                                    event,
                                    categoryTemplates.length,
                                    index,
                                    (i) =>
                                      setTemplateId(categoryTemplates[i].id),
                                  )
                                }
                              >
                                <div className={styles.methodCardRadio}>
                                  {isSelected ? (
                                    <CheckmarkCircleFilled
                                      className={styles.radioCheckmark}
                                      aria-hidden="true"
                                    />
                                  ) : (
                                    <div
                                      className={styles.radioCircle}
                                      aria-hidden="true"
                                    />
                                  )}
                                </div>
                                <span className={styles.templateCardName}>
                                  {template.name}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {activeStep === "question-bank-upload" && (
                <div
                  ref={questionBankPanelRef}
                  className={contentStyles.tabContent}
                  role="tabpanel"
                  id="tabpanel-question-bank-upload"
                  aria-labelledby="tab-question-bank-upload"
                >
                  <p className={styles.methodHelperText}>
                    {DIALOG_LABELS.QUESTION_BANK_UPLOAD_HELPER}
                  </p>
                  <Field
                    label={
                      mode === "edit" &&
                        diagnostic?.customQuestionBankUrl &&
                        !isCustomQuestionBankRemoved &&
                        !questionBankFile
                        ? DIALOG_LABELS.QUESTION_BANK_FILE_LABEL_EXISTING
                        : DIALOG_LABELS.QUESTION_BANK_FILE_LABEL
                    }
                  >
                    {(() => {
                      const hasFile =
                        questionBankFile !== null ||
                        (mode === "edit" &&
                          !!diagnostic?.customQuestionBankUrl &&
                          !isCustomQuestionBankRemoved);
                      const existingFileName =
                        diagnostic?.customQuestionBankUrl
                          ?.split("?")[0]
                          .split("/")
                          .pop() ?? "question-bank.xlsx";

                      return (
                        <>
                          <FileDropzone
                            onFilesSelected={(files) =>
                              setQuestionBankFile(files[0] ?? null)
                            }
                            acceptedExtensions={QUESTION_BANK_EXTENSIONS}
                            multiple={false}
                            disabled={isSaving || hasFile}
                            disabledMessage={
                              hasFile
                                ? DIALOG_LABELS.QUESTION_BANK_MAX_ONE
                                : undefined
                            }
                          />
                          {hasFile && (
                            <div className={styles.questionBankFileList}>
                              <div className={styles.questionBankFileCard}>
                                <div className={styles.questionBankFileIconBox}>
                                  <DocumentRegular aria-hidden="true" />
                                </div>
                                <div className={styles.questionBankFileMeta}>
                                  <span className={styles.questionBankFileName}>
                                    {questionBankFile
                                      ? questionBankFile.name
                                      : existingFileName}
                                  </span>
                                  <span
                                    className={styles.questionBankFileDetails}
                                  >
                                    {questionBankFile
                                      ? [
                                        getFileTypeInfo(questionBankFile.name)
                                          .label,
                                        formatFileSize(questionBankFile.size),
                                        formatDate(
                                          questionBankFile.lastModified /
                                          1000,
                                        ),
                                      ].join(" · ")
                                      : [
                                        getFileTypeInfo(existingFileName)
                                          .label,
                                        existingQuestionBankMeta?.size
                                          ? formatFileSize(
                                            existingQuestionBankMeta.size,
                                          )
                                          : null,
                                        existingQuestionBankMeta?.lastModified
                                          ? formatDate(
                                            existingQuestionBankMeta.lastModified /
                                            1000,
                                          )
                                          : null,
                                      ]
                                        .filter(Boolean)
                                        .join(" · ")}
                                  </span>
                                </div>
                                <Button
                                  appearance="subtle"
                                  icon={<DismissRegular aria-hidden="true" />}
                                  aria-label={
                                    questionBankFile
                                      ? `Remove ${questionBankFile.name}`
                                      : "Remove question bank file"
                                  }
                                  className={fileCardStyles.fileDeleteButton}
                                  onClick={() => {
                                    if (questionBankFile) {
                                      setQuestionBankFile(null);
                                    } else {
                                      setIsCustomQuestionBankRemoved(true);
                                    }
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </Field>
                </div>
              )}

              {activeStep === "demographics" && (
                <div
                  className={contentStyles.tabContent}
                  role="tabpanel"
                  id="tabpanel-demographics"
                  aria-labelledby="tab-demographics"
                >
                  <DemographicsTab
                    selectedQuestions={demographicsQuestions}
                    onChange={setDemographicsQuestions}
                    placement={demographicsPlacement}
                    onPlacementChange={setDemographicsPlacement}
                    overrides={demographicsQuestionOverrides}
                    onOverrideChange={setDemographicsQuestionOverrides}
                    disabled={isSaving}
                  />
                </div>
              )}

              {activeStep === "branding" && (
                <div
                  className={contentStyles.tabContent}
                  role="tabpanel"
                  id="tabpanel-branding"
                  aria-labelledby="tab-branding"
                >
                  <p className={styles.methodHelperText}>
                    {DIALOG_LABELS.BRANDING_HELPER}
                  </p>
                  <div className={styles.brandingLayout}>
                    <div className={styles.brandingFormColumn}>
                      <Field label={DIALOG_LABELS.HEADER_LOGO_LABEL}>
                        {headerLogoUrl ? (
                          <div className={styles.uploadedFileRow}>
                            <img
                              src={headerLogoUrl}
                              alt={DIALOG_LABELS.LOGO_PREVIEW_ALT}
                              className={styles.logoPreview}
                            />
                            <button
                              type="button"
                              className={styles.removeFileButton}
                              aria-label={DIALOG_LABELS.REMOVE_LOGO}
                              onClick={handleLogoFileRemove}
                              disabled={isSaving}
                            >
                              <DismissRegular aria-hidden="true" />
                            </button>
                          </div>
                        ) : (
                          <FileDropzone
                            onFilesSelected={handleLogoFileSelect}
                            acceptedExtensions={LOGO_EXTENSIONS}
                            multiple={false}
                            disabled={isSaving}
                          />
                        )}
                      </Field>
                      <Field label={DIALOG_LABELS.HEADER_COLOR_LABEL}>
                        <div className={styles.colorPickerRow}>
                          <input
                            type="color"
                            value={headerColor}
                            onChange={(e) => setHeaderColor(e.target.value)}
                            aria-label={DIALOG_LABELS.HEADER_COLOR_PICKER}
                            disabled={isSaving}
                            className={styles.colorInput}
                          />
                          <Input
                            value={headerColor}
                            maxLength={7}
                            onChange={(_, d) => setHeaderColor(d.value)}
                            disabled={isSaving}
                            aria-label={DIALOG_LABELS.HEADER_COLOR_HEX}
                            className={styles.hexInput}
                          />
                          <Button
                            appearance="subtle"
                            size="small"
                            onClick={() => setHeaderColor(DEFAULT_HEADER_COLOR)}
                            disabled={
                              isSaving || headerColor === DEFAULT_HEADER_COLOR
                            }
                          >
                            {DIALOG_LABELS.RESET_TO_DEFAULT}
                          </Button>
                        </div>
                      </Field>
                      <Field label={DIALOG_LABELS.ACCENT_COLOR_LABEL}>
                        <div className={styles.colorPickerRow}>
                          <input
                            type="color"
                            value={accentColor}
                            onChange={(e) => setAccentColor(e.target.value)}
                            aria-label={DIALOG_LABELS.ACCENT_COLOR_PICKER}
                            disabled={isSaving}
                            className={styles.colorInput}
                          />
                          <Input
                            value={accentColor}
                            maxLength={7}
                            onChange={(_, d) => setAccentColor(d.value)}
                            disabled={isSaving}
                            aria-label={DIALOG_LABELS.ACCENT_COLOR_HEX}
                            className={styles.hexInput}
                          />
                          <Button
                            appearance="subtle"
                            size="small"
                            onClick={() => setAccentColor(DEFAULT_ACCENT_COLOR)}
                            disabled={
                              isSaving ||
                              accentColor.toLowerCase() === DEFAULT_ACCENT_COLOR
                            }
                          >
                            {DIALOG_LABELS.RESET_TO_DEFAULT}
                          </Button>
                        </div>
                      </Field>
                    </div>
                    <div className={styles.brandingPreviewColumn}>
                      <p className={styles.brandingPreviewLabel}>Preview</p>
                      <BrandingPreview
                        headerColor={headerColor}
                        headerLogoUrl={headerLogoUrl || undefined}
                        accentPalette={buildAccentPalette(
                          accentColor,
                          headerColor,
                        )}
                      />
                    </div>
                  </div>
                </div>
              )}

              {saveError && (
                <div role="alert" className={contentStyles.feedbackErrorBox}>
                  {saveError}
                </div>
              )}
            </div>
          </DialogContent>
        </DialogBody>
        {!isSaving && (
          <div className={footerStyles.dialogFooter}>
            {activeStep !== "basic-info" && (
              <DialogFooterAction
                icon={<ArrowLeftRegular />}
                tooltip={DIALOG_LABELS.BACK}
                onClick={handleBack}
              />
            )}
            {!isLastStep && (
              <DialogFooterAction
                icon={<ArrowRightRegular />}
                tooltip={DIALOG_LABELS.NEXT}
                dimmed={isNextDisabled}
                ariaLabel={
                  isNextDisabled
                    ? DIALOG_LABELS.NEXT_DISABLED
                    : DIALOG_LABELS.NEXT
                }
                onClick={handleNext}
              />
            )}
            {isLastStep && (
              <span ref={createButtonRef} tabIndex={-1}>
                <DialogFooterAction
                  icon={<CheckmarkRegular />}
                  tooltip={
                    mode === "edit"
                      ? DIALOG_LABELS.SUBMIT_LABEL_EDIT
                      : DIALOG_LABELS.SUBMIT_LABEL
                  }
                  dimmed={isSubmitDisabled}
                  disabled={isSubmitDisabled}
                  ariaLabel={
                    isSubmitDisabled
                      ? mode === "edit"
                        ? DIALOG_LABELS.SUBMIT_LABEL_EDIT_DISABLED
                        : DIALOG_LABELS.SUBMIT_LABEL_DISABLED
                      : mode === "edit"
                        ? DIALOG_LABELS.SUBMIT_LABEL_EDIT
                        : DIALOG_LABELS.SUBMIT_LABEL
                  }
                  onClick={handleSubmit}
                />
              </span>
            )}
          </div>
        )}
      </DialogSurface>
    </Dialog>
  );
};
